import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { UIControlType } from '@stormwater-software/lib-form-control-types';
import inspectionApi from '../../../utils/api/inspection';
import ScopedSchemaFields from './ScopedSchemaFields';

/** SW2-2539 — the client's certification date locks, one per signature group. */
export const CERT_DATE_LOCK_FIELDS = [
  { key: 'lock_inspection_cert_date', label: 'Inspection' },
  { key: 'lock_acknowledgement_cert_date', label: 'Acknowledgement' },
  { key: 'lock_compliance_cert_date', label: 'Compliance' },
];

/** The form's starting values for the locks, from the client record. */
export const certDateLockInitialData = (client) =>
  Object.fromEntries(CERT_DATE_LOCK_FIELDS.map(({ key }) => [key, !!client?.[key]]));

/**
 * The three choices, stacked one per row under the main checkbox. Not `responsive-form-checkbox`:
 * that class forces each checkbox to a quarter of the row (`flex: 0 0 25% !important`).
 */
const schema = Object.fromEntries(
  CERT_DATE_LOCK_FIELDS.map(({ key, label }) => [
    key,
    {
      controlType: UIControlType.checkbox,
      label,
      style: { flex: '0 0 100%', marginBottom: '0.5rem' },
    },
  ]),
);

/** The main checkbox, drawn by the same form library as the section's other checkboxes. */
const mainSchema = (lockedByAdmin) => ({
  lock_certification_dates: {
    controlType: UIControlType.checkbox,
    label: 'Lock Certification Dates',
    className: 'responsive-form-checkbox',
    disabled: lockedByAdmin,
    toolTipOptions: {
      message: lockedByAdmin
        ? 'Locked by Admin: certification dates are locked to today for all clients.'
        : 'Choose which certifications on this client\'s projects default to today and can only be dated today; a backdated one is refused. Separate from Disable Missed Inspection Backdating, which only affects how late inspections are counted as missed.',
    },
    style: { flexBasis: '100%' },
  },
});

/**
 * SW2-2539 — "Lock Certification Dates", a group inside the client's Inspections settings: which
 * signature groups' dates are locked to today on every project under this client (projects added
 * later too).
 *
 * One main checkbox opens the three choices, drawn like the section's other checkboxes with its
 * explanation in a tooltip. It isn't stored: it starts ticked when any group is locked, and unticking
 * it clears all three, so off always means nothing is locked.
 *
 * Shown only while the tenant has "Enable client-level certification date locks" on (Administration
 * → Tenant Inspection Details). While the tenant's "Lock certification dates for all clients" is on,
 * every group is locked whatever these say, so the group says that instead of offering checkboxes.
 * Worded apart from "Disable Missed Inspection Backdating" just above it, which only changes how late
 * inspections count as missed in reporting.
 */
const CertificationDateLocks = ({ client, formContext }) => {
  const tenantQuery = useQuery({
    queryKey: ['tenantBackdatingDetails'],
    queryFn: () => inspectionApi.getTenantBackdatingDetails(),
    refetchOnWindowFocus: false,
  });
  const tenant = tenantQuery.data;
  const values = formContext.value || {};
  const anyLocked = CERT_DATE_LOCK_FIELDS.some(({ key }) => !!values[key]);
  const [open, setOpen] = useState(() =>
    CERT_DATE_LOCK_FIELDS.some(({ key }) => !!client?.[key]),
  );

  if (!tenant?.enable_client_cert_date_locks) return null;

  const lockedByAdmin = !!tenant.disable_inspection_backdating;

  const toggle = (checked) => {
    // The form library replays initial values through onChange on mount, so only an actual
    // untick (it was open) clears the three — off means nothing is locked, no hidden ticks left.
    if (!checked && open) CERT_DATE_LOCK_FIELDS.forEach(({ key }) => formContext.set(key, false));
    setOpen(checked);
  };
  const mainValue = { lock_certification_dates: lockedByAdmin || open };

  return (
    <div>
      <ScopedSchemaFields
        schema={mainSchema(lockedByAdmin)}
        formData={mainValue}
        initialFormData={mainValue}
        onChange={(_key, checked) => toggle(!!checked)}
      />
      {lockedByAdmin && (
        <p className='col-danger' style={{ fontSize: '12px', margin: '0 0 8px 24px' }}>
          Locked by Admin: certification dates are locked to today for all clients.
        </p>
      )}
      {!lockedByAdmin && open && (
        <div style={{ marginLeft: '24px' }}>
          <ScopedSchemaFields
            schema={schema}
            onChange={formContext.set}
            formData={formContext.value}
            // The CURRENT form values, not the saved client: lib-form writes initial values back on
            // mount, so re-ticking the main box must not restore locks the user just cleared.
            initialFormData={certDateLockInitialData(formContext.value)}
          />
          {!anyLocked && (
            <p className='col-danger' style={{ fontSize: '12px', margin: '0 0 8px' }}>
              Tick at least one. Nothing is locked until you do.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default CertificationDateLocks;
